import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Button,
} from "@chakra-ui/react"
import { DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"
import { statusLabel, statusColor, isRangedStatus } from "./statusCodes"

const ArticleStatus = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [summaryData, setSummaryData] = useState([])
  const [detailData, setDetailData] = useState([])
  const [downloading, setDownloading] = useState(false)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const kpiBg = useColorModeValue("teal.50", "teal.900")
  const warnBg = useColorModeValue("orange.50", "orange.900")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/insights/article-status`,
        {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        },
        { signal }
      )
      if (res.data.status === 1) {
        setSummaryData(res.data.data.summary || [])
        setDetailData(res.data.data.detail || [])
      } else {
        setSummaryData([])
        setDetailData([])
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("ArticleStatus fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  const formatNum = (val) => (parseInt(val) || 0).toLocaleString()

  // Every retailer has its own codes (statusCodes.js, 2026-10-07).
  const getStatusLabel = (code) => statusLabel(code, retailer)
  const getStatusColor = (code) => statusColor(code, retailer)

  const rangedRows = summaryData.filter((r) => isRangedStatus(r.status_code, retailer))
  const activeRow = rangedRows.length
    ? rangedRows.reduce((t, r) => ({ ...t, article_count: Number(t.article_count || 0) + Number(r.article_count || 0), store_count: Math.max(Number(t.store_count || 0), Number(r.store_count || 0)), total_soh_qty: Number(t.total_soh_qty || 0) + Number(r.total_soh_qty || 0), total_soh_value: Number(t.total_soh_value || 0) + Number(r.total_soh_value || 0) }), {})
    : undefined
  const nonActiveRows = summaryData.filter((r) => !isRangedStatus(r.status_code, retailer))
  const totalNonActiveValue = nonActiveRows.reduce((sum, r) => sum + (parseFloat(r.total_soh_value) || 0), 0)
  const totalNonActiveQty = nonActiveRows.reduce((sum, r) => sum + (parseInt(r.total_soh_qty) || 0), 0)

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const workbook = new ExcelJS.Workbook()

      // Summary sheet
      const ws1 = workbook.addWorksheet("Status Summary")
      ws1.columns = [
        { header: "Status Code", key: "status_code", width: 12 },
        { header: "Status", key: "status_label", width: 30 },
        { header: "Articles", key: "article_count", width: 10 },
        { header: "Stores", key: "store_count", width: 10 },
        { header: "SOH Qty", key: "total_soh_qty", width: 12 },
        { header: "SOH Value", key: "total_soh_value", width: 15 },
      ]
      summaryData.forEach((row) => {
        ws1.addRow({
          status_code: row.status_code,
          status_label: getStatusLabel(row.status_code),
          article_count: parseInt(row.article_count) || 0,
          store_count: parseInt(row.store_count) || 0,
          total_soh_qty: parseInt(row.total_soh_qty) || 0,
          total_soh_value: parseFloat(row.total_soh_value) || 0,
        })
      })
      ws1.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
      })

      // Detail sheet (non-active articles with SOH)
      if (detailData.length > 0) {
        const ws2 = workbook.addWorksheet("Not ranged with SOH")
        ws2.columns = [
          { header: "Article Code", key: "article_code", width: 15 },
          { header: "Description", key: "articledescription", width: 35 },
          { header: "Brand", key: "brand", width: 18 },
          { header: "Status Code", key: "status_code", width: 12 },
          { header: "Status", key: "status_label", width: 25 },
          { header: "Stores", key: "store_count", width: 10 },
          { header: "SOH Qty", key: "total_soh_qty", width: 12 },
          { header: "SOH Value", key: "total_soh_value", width: 15 },
        ]
        detailData.forEach((row) => {
          ws2.addRow({
            article_code: row.article_code,
            articledescription: row.articledescription,
            brand: row.brand || "-",
            status_code: row.status_code,
            status_label: getStatusLabel(row.status_code),
            store_count: parseInt(row.store_count) || 0,
            total_soh_qty: parseInt(row.total_soh_qty) || 0,
            total_soh_value: parseFloat(row.total_soh_value) || 0,
          })
        })
        ws2.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws2.getColumn("total_soh_value").numFmt = "#,##0.00"
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `Article_Status_${supplier}_${retailer}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const hasData = summaryData.length > 0

  return (
    <Box>

      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        {hasData && (
          <Button
            size="sm"
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            onClick={handleDownload}
            isLoading={downloading}
            loadingText="Downloading"
          >
            Download Report
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : !hasData ? (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">
            No article status data found
          </Text>
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Ranged SOH Value</StatLabel>
              <StatNumber fontSize="lg" color="green.600">
                {formatCurrency(activeRow?.total_soh_value || 0)}
              </StatNumber>
              <Text fontSize="xs" color="gray.500">
                {formatNum(activeRow?.article_count || 0)} articles in {formatNum(activeRow?.store_count || 0)} stores
              </Text>
            </Stat>
            <Stat bg={warnBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Not-ranged SOH Value</StatLabel>
              <StatNumber fontSize="lg" color="orange.600">
                {formatCurrency(totalNonActiveValue)}
              </StatNumber>
              <Text fontSize="xs" color="gray.500">
                Capital in discontinued/delisted stock
              </Text>
            </Stat>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Not-ranged SOH Units</StatLabel>
              <StatNumber fontSize="lg">{formatNum(totalNonActiveQty)}</StatNumber>
              <Text fontSize="xs" color="gray.500">
                {detailData.length} article-status combinations
              </Text>
            </Stat>
          </SimpleGrid>

          {/* Status Breakdown Table */}
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" mb={4}>
            <Box p={3} borderBottom="1px" borderColor={borderColor}>
              <Heading size="sm">Status Breakdown</Heading>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Article counts and SOH value per status code
              </Text>
            </Box>
            <Table size="sm" variant="simple">
              <Thead bg={headerBg}>
                <Tr>
                  <Th>Status</Th>
                  <Th>Code</Th>
                  <Th isNumeric>Articles</Th>
                  <Th isNumeric>Stores</Th>
                  <Th isNumeric>SOH Qty</Th>
                  <Th isNumeric>SOH Value</Th>
                </Tr>
              </Thead>
              <Tbody>
                {summaryData.map((row) => (
                  <Tr key={row.status_code} _hover={{ bg: hoverBg }}>
                    <Td fontSize="xs">
                      <Badge colorScheme={getStatusColor(row.status_code)} fontSize="2xs" mr={2}>
                        {row.status_code}
                      </Badge>
                      {getStatusLabel(row.status_code)}
                    </Td>
                    <Td fontSize="xs">{row.status_code}</Td>
                    <Td isNumeric fontSize="xs">
                      {formatNum(row.article_count)}
                    </Td>
                    <Td isNumeric fontSize="xs">
                      {formatNum(row.store_count)}
                    </Td>
                    <Td isNumeric fontSize="xs">
                      {formatNum(row.total_soh_qty)}
                    </Td>
                    <Td isNumeric fontSize="xs" fontWeight="bold" color={isRangedStatus(row.status_code, retailer) ? "green.600" : "orange.500"}>
                      {formatCurrency(row.total_soh_value)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Non-Active Articles with SOH Detail */}
          {detailData.length > 0 && (
            <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
              <Box p={3} borderBottom="1px" borderColor={borderColor}>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm">Not-ranged Articles with Stock</Heading>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Discontinued or delisted articles that still have SOH in stores — potential markdown or return candidates
                    </Text>
                  </Box>
                  <Badge colorScheme="orange">{detailData.length}</Badge>
                </Flex>
              </Box>
              <Box maxH="400px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Article</Th>
                      <Th>Brand</Th>
                      <Th>Status</Th>
                      <Th isNumeric>Stores</Th>
                      <Th isNumeric>SOH Qty</Th>
                      <Th isNumeric>SOH Value</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {detailData.map((row, i) => (
                      <Tr key={i} _hover={{ bg: hoverBg }}>
                        <Td fontSize="xs" maxW="220px" isTruncated>
                          {row.articledescription}
                        </Td>
                        <Td fontSize="xs">{row.brand || "-"}</Td>
                        <Td fontSize="xs">
                          <Badge colorScheme={getStatusColor(row.status_code)} fontSize="2xs">
                            {row.status_code}
                          </Badge>{" "}
                          <Text as="span" fontSize="2xs" color="gray.500">
                            {getStatusLabel(row.status_code)}
                          </Text>
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {formatNum(row.store_count)}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {formatNum(row.total_soh_qty)}
                        </Td>
                        <Td isNumeric fontSize="xs" fontWeight="bold" color="orange.500">
                          {formatCurrency(row.total_soh_value)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default ArticleStatus
